/**
 * useChildMutations hook
 * React Query mutations for creating, renaming, and resetting child profile passwords.
 *
 * Implements US-004 (T2): useChildMutations with create + rename mutations.
 * Modified US-002 (T-003): added resetPassword mutation slot.
 * Directly follows the useCompleteSession useMutation+invalidateQueries pattern
 * (frontend/src/hooks/useCompleteSession.ts).
 *
 * TO-LL-044 enforcement: mutation variables use exact camelCase keys displayName/
 * password/username matching children.py (enforced via CreateChildPayload type).
 * TO-LL-036 enforcement: errors are never caught inside mutations; onSuccess-only
 * invalidation ensures a failed mutation never triggers a stale-then-correct refetch.
 *
 * On success, all mutations invalidate ['parent','dashboard'] so useParentDashboard
 * refetches and ChildProfilesGrid shows the updated children list automatically
 * (confirmed: useParentDashboard.ts line 74 uses queryKey ['parent','dashboard']).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createChild, renameChild, resetChildPassword } from '@/services/children';
import type { CreateChildPayload, CreatedChild, RenamedChild, ResetChildPasswordResult } from '@/types/children';
import type { AuthError } from '@/types/auth';

// ─── Mutation variable types ──────────────────────────────────────────────────

/** Variables for the rename mutation: childId + new displayName */
export interface RenameChildVariables {
  /** The child's userId (Cognito sub) */
  childId: string;
  /** New display name (trimmed by dialog before mutate call) */
  displayName: string;
}

/**
 * Variables for the resetPassword mutation: childId + new password.
 * Implements US-002 (T-003). TO-LL-044: `password` key exactly matches backend reset body.
 */
export interface ResetChildPasswordVariables {
  /** The child's userId (Cognito sub) */
  childId: string;
  /**
   * New password (validated client-side by validatePassword before mutate call).
   * Security: never logged; never stored beyond component state.
   */
  password: string;
}

// ─── Hook return type ─────────────────────────────────────────────────────────

/**
 * Shape of each mutation slot exposed by useChildMutations.
 * Mirrors the useMutation API surface the dialogs need to drive their UI.
 */
export interface ChildMutationSlot<TVariables, _TData> {
  /** Trigger the mutation */
  mutate: (variables: TVariables) => void;
  /** True while the mutation request is in-flight (disables dialog buttons) */
  isPending: boolean;
  /** Error from the axios interceptor: {code, message}; null when no error */
  error: AuthError | null;
  /** True after the mutation succeeded (triggers dialog close via useEffect) */
  isSuccess: boolean;
  /** Reset mutation state (called when dialog reopens to clear stale error) */
  reset: () => void;
}

export interface UseChildMutationsReturn {
  /** Create-child mutation slot */
  create: ChildMutationSlot<CreateChildPayload, CreatedChild>;
  /** Rename-child mutation slot */
  rename: ChildMutationSlot<RenameChildVariables, RenamedChild>;
  /** Reset-child-password mutation slot (US-002) */
  resetPassword: ChildMutationSlot<ResetChildPasswordVariables, ResetChildPasswordResult>;
}

// ─── Hook implementation ──────────────────────────────────────────────────────

/**
 * React Query mutations for child profile management.
 *
 * All mutations invalidate ['parent','dashboard'] on success only —
 * TO-LL-036: onSuccess-only ensures a failed mutation never refetches and
 * presents stale-correct data that obscures the error.
 *
 * @returns { create, rename, resetPassword } — each with { mutate, isPending, error, isSuccess, reset }
 */
export function useChildMutations(): UseChildMutationsReturn {
  const queryClient = useQueryClient();

  /**
   * Create-child mutation.
   * mutationFn: (payload: CreateChildPayload) => createChild(payload)
   * onSuccess: invalidates ['parent','dashboard'] so ChildProfilesGrid refetches.
   * TO-LL-044: payload keys are exactly displayName/password/username (enforced by type).
   * TO-LL-036: errors propagate from service layer; no try/catch here.
   */
  const createMutation = useMutation<CreatedChild, AuthError, CreateChildPayload>({
    mutationFn: (payload) => createChild(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent', 'dashboard'] });
    },
  });

  /**
   * Rename-child mutation.
   * mutationFn: ({childId, displayName}) => renameChild(childId, displayName)
   * onSuccess: invalidates ['parent','dashboard'] so grid shows new name.
   * TO-LL-044: only displayName sent to PATCH /children/{childId} (userId/username never touched).
   * TO-LL-036: errors propagate; onSuccess-only invalidation.
   */
  const renameMutation = useMutation<RenamedChild, AuthError, RenameChildVariables>({
    mutationFn: ({ childId, displayName }) => renameChild(childId, displayName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent', 'dashboard'] });
    },
  });

  /**
   * Reset-child-password mutation.
   * Implements US-002 (T-003). Mirrors renameMutation slot shape exactly.
   * mutationFn: ({childId, password}) => resetChildPassword(childId, password)
   * onSuccess: invalidates ['parent','dashboard'] so grid refetches.
   * TO-LL-044: body key is exactly `password` (enforced by ResetChildPasswordVariables type).
   * TO-LL-036: errors propagate; onSuccess-only invalidation — no onError swallow.
   *            The axios interceptor (api.ts:50-57) already normalises NETWORK_ERROR;
   *            no additional onError handler needed.
   */
  const resetPasswordMutation = useMutation<ResetChildPasswordResult, AuthError, ResetChildPasswordVariables>({
    mutationFn: ({ childId, password }) => resetChildPassword(childId, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent', 'dashboard'] });
    },
  });

  return {
    create: {
      mutate: createMutation.mutate,
      isPending: createMutation.isPending,
      error: createMutation.error,
      isSuccess: createMutation.isSuccess,
      reset: createMutation.reset,
    },
    rename: {
      mutate: renameMutation.mutate,
      isPending: renameMutation.isPending,
      error: renameMutation.error,
      isSuccess: renameMutation.isSuccess,
      reset: renameMutation.reset,
    },
    resetPassword: {
      mutate: resetPasswordMutation.mutate,
      isPending: resetPasswordMutation.isPending,
      error: resetPasswordMutation.error,
      isSuccess: resetPasswordMutation.isSuccess,
      reset: resetPasswordMutation.reset,
    },
  };
}
